import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import SiteRuntime from './components/SiteRuntime';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Essential pages - loaded immediately for fastest initial load
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Dashboard loaded immediately since it's the main destination after login
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SinolinkPayPage = lazy(() => import('./pages/SinolinkPayPage'));
const AdminCentre = lazy(() => import('./pages/admin/AdminCentre'));

// Lazy loaded pages for better performance - chunked by feature
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const ProcurementPage = lazy(() => import('./pages/ProcurementPage'));
const TrackingPage = lazy(() => import('./pages/TrackingPage'));
const WarehousePage = lazy(() => import('./pages/WarehousePage'));
const WarehouseAddressPage = lazy(() => import('./pages/WarehouseAddressPage'));
const ConsolidationPage = lazy(() => import('./pages/ConsolidationPage'));
const ShippingPage = lazy(() => import('./pages/ShippingPage'));
const BarcodeScannerPage = lazy(() => import('./pages/BarcodeScannerPage'));
const RatesPage = lazy(() => import('./pages/RatesPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const AgentRegisterPage = lazy(() => import('./pages/AgentRegisterPage'));

// Shop/Ecommerce pages
const ShopPage = lazy(() => import('./pages/ShopPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'));
const GuestCheckoutCallback = lazy(() => import('./pages/GuestCheckoutCallback'));
const MembershipCallbackPage = lazy(() => import('./pages/MembershipCallbackPage'));
const PaymentCallbackPage = lazy(() => import('./pages/PaymentCallbackPage'));

// Agent pages
const AgentDashboard = lazy(() => import('./pages/agent/AgentDashboard'));
const AgentCustomers = lazy(() => import('./pages/agent/AgentCustomers'));

// Admin pages - all lazy loaded
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDashboard = lazy(() => import('./pages/admin/AdminUserDashboard'));
const AdminProcurement = lazy(() => import('./pages/admin/AdminProcurement'));
const AdminTracking = lazy(() => import('./pages/admin/AdminTracking'));
const AdminWarehouse = lazy(() => import('./pages/admin/AdminWarehouse'));
const AdminScanStation = lazy(() => import('./pages/admin/AdminScanStation'));
const AdminShipments = lazy(() => import('./pages/admin/AdminShipments'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminConsolidations = lazy(() => import('./pages/admin/AdminConsolidations'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminAgents = lazy(() => import('./pages/admin/AdminAgents'));
const AdminHistory = lazy(() => import('./pages/admin/AdminHistory'));
const AdminBankTransfers = lazy(() => import('./pages/admin/AdminBankTransfers'));
const AdminAlipayTransfers = lazy(() => import('./pages/admin/AdminAlipayTransfers'));
const AdminEcommerce = lazy(() => import('./pages/admin/AdminEcommerce'));
const AdminTraining = lazy(() => import('./pages/admin/AdminTraining'));
const AdminUserAnalytics = lazy(() => import('./pages/admin/AdminUserAnalytics'));
const AdminUserControlCenter = lazy(() => import('./pages/admin/AdminUserControlCenter'));
const AdminRefunds = lazy(() => import('./pages/admin/AdminRefunds'));
const AdminMarketing = lazy(() => import('./pages/admin/AdminMarketing'));
const AdminClientTracking = lazy(() => import('./pages/admin/AdminClientTracking'));
const AdminWarehouseAddresses = lazy(() => import('./pages/admin/AdminWarehouseAddresses'));
const AdminMembers = lazy(() => import('./pages/admin/AdminMembers'));

// Training and Guide pages
const TrainingPage = lazy(() => import('./pages/TrainingPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));

// Optimized loading component for lazy loaded pages
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-accent">
      <div className="text-center text-white">
        <h1 className="text-3xl font-black mb-4">SINOLINK</h1>
        <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    </div>
  );
}

// Minimal initial loader for auth check
function AuthLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-accent">
      <div className="text-center text-white">
        <h1 className="text-3xl font-black mb-4">SINOLINK</h1>
        <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, requireAdmin = false, requireAgent = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <AuthLoader />;
  }

  if (!user) {
    // Redirect to home page instead of login - user can choose to login from there
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireAgent && user.role !== 'agent') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  // Redirect based on role
  const getDefaultRoute = () => {
    if (!user) return '/';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'agent') return '/agent';
    return '/dashboard';
  };

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={user ? <Navigate to={getDefaultRoute()} /> : <LandingPage />} />
        <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} /> : <LoginPage />} />
        <Route path="/register" element={user ? <Navigate to={getDefaultRoute()} /> : <RegisterPage />} />
        <Route path="/forgot-password" element={user ? <Navigate to={getDefaultRoute()} /> : <ForgotPasswordPage />} />
        <Route path="/guest-checkout-callback" element={<GuestCheckoutCallback />} />
        <Route path="/membership/callback" element={<MembershipCallbackPage />} />
        <Route path="/shipping/payment-callback" element={<ProtectedRoute><PaymentCallbackPage type="shipping" /></ProtectedRoute>} />
        <Route path="/consolidation/payment-callback" element={<ProtectedRoute><PaymentCallbackPage type="consolidation" /></ProtectedRoute>} />
        <Route path="/procurement/payment-callback" element={<ProtectedRoute><PaymentCallbackPage type="procurement" /></ProtectedRoute>} />
        <Route path="/reset-password" element={user ? <Navigate to={getDefaultRoute()} /> : <ResetPasswordPage />} />
        <Route path="/agent/register" element={user ? <Navigate to={getDefaultRoute()} /> : <AgentRegisterPage />} />
        
        {/* Client Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
        <Route path="/procurement" element={<ProtectedRoute><ProcurementPage /></ProtectedRoute>} />
        <Route path="/tracking" element={<ProtectedRoute><TrackingPage /></ProtectedRoute>} />
        <Route path="/scan" element={<ProtectedRoute><BarcodeScannerPage /></ProtectedRoute>} />
        <Route path="/warehouse" element={<ProtectedRoute><WarehousePage /></ProtectedRoute>} />
        <Route path="/warehouse-address" element={<ProtectedRoute><WarehouseAddressPage /></ProtectedRoute>} />
        <Route path="/consolidation" element={<ProtectedRoute><ConsolidationPage /></ProtectedRoute>} />
        <Route path="/shipping" element={<ProtectedRoute><ShippingPage /></ProtectedRoute>} />
        <Route path="/rates" element={<ProtectedRoute><RatesPage /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
        
        {/* Shop/Ecommerce Routes */}
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/sinolink-pay" element={<ProtectedRoute><SinolinkPayPage /></ProtectedRoute>} />
        <Route path="/shop/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
        <Route path="/shop/product/:productId" element={<ProductDetailPage />} />
        <Route path="/order-success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
        
        {/* Training & Guide Routes */}
        <Route path="/training" element={<ProtectedRoute><TrainingPage /></ProtectedRoute>} />
        <Route path="/training/:slug" element={<ProtectedRoute><TrainingPage /></ProtectedRoute>} />
        <Route path="/guide" element={<ProtectedRoute><GuidePage /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
        
        {/* Agent Routes */}
        <Route path="/agent" element={<ProtectedRoute requireAgent><AgentDashboard /></ProtectedRoute>} />
        <Route path="/agent/customers" element={<ProtectedRoute requireAgent><AgentCustomers /></ProtectedRoute>} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute requireAdmin><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/users/:userId/dashboard" element={<ProtectedRoute requireAdmin><AdminUserDashboard /></ProtectedRoute>} />
        <Route path="/admin/users/:userId/analytics" element={<ProtectedRoute requireAdmin><AdminUserAnalytics /></ProtectedRoute>} />
        <Route path="/admin/users/:userId/control" element={<ProtectedRoute requireAdmin><AdminUserControlCenter /></ProtectedRoute>} />
        <Route path="/admin/agents" element={<ProtectedRoute requireAdmin><AdminAgents /></ProtectedRoute>} />
        <Route path="/admin/procurement" element={<ProtectedRoute requireAdmin><AdminProcurement /></ProtectedRoute>} />
        <Route path="/admin/tracking" element={<ProtectedRoute requireAdmin><AdminTracking /></ProtectedRoute>} />
        <Route path="/admin/warehouse" element={<ProtectedRoute requireAdmin><AdminWarehouse /></ProtectedRoute>} />
        <Route path="/admin/scan-station" element={<ProtectedRoute requireAdmin><AdminScanStation /></ProtectedRoute>} />
        <Route path="/admin/warehouse-addresses" element={<ProtectedRoute requireAdmin><AdminWarehouseAddresses /></ProtectedRoute>} />
        <Route path="/admin/shipments" element={<ProtectedRoute requireAdmin><AdminShipments /></ProtectedRoute>} />
        <Route path="/admin/consolidations" element={<ProtectedRoute requireAdmin><AdminConsolidations /></ProtectedRoute>} />
        <Route path="/admin/history" element={<ProtectedRoute requireAdmin><AdminHistory /></ProtectedRoute>} />
        <Route path="/admin/bank-transfers" element={<ProtectedRoute requireAdmin><AdminBankTransfers /></ProtectedRoute>} />
        <Route path="/admin/alipay-transfers" element={<ProtectedRoute requireAdmin><AdminAlipayTransfers /></ProtectedRoute>} />
        <Route path="/admin/refunds" element={<ProtectedRoute requireAdmin><AdminRefunds /></ProtectedRoute>} />
        <Route path="/admin/marketing" element={<ProtectedRoute requireAdmin><AdminMarketing /></ProtectedRoute>} />
        <Route path="/admin/centre" element={<ProtectedRoute requireAdmin><AdminCentre /></ProtectedRoute>} />
        <Route path="/admin/client-tracking" element={<ProtectedRoute requireAdmin><AdminClientTracking /></ProtectedRoute>} />
        <Route path="/admin/members" element={<ProtectedRoute requireAdmin><AdminMembers /></ProtectedRoute>} />
        <Route path="/admin/ecommerce" element={<ProtectedRoute requireAdmin><AdminEcommerce /></ProtectedRoute>} />
        <Route path="/admin/training" element={<ProtectedRoute requireAdmin><AdminTraining /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute requireAdmin><AdminSettings /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <SiteRuntime />
          <AppRoutes />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
